import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NAME_VALIDATION, isValidName } from '@/lib/utils';

interface ProjectNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
  title?: string;
  defaultValue?: string;
  submitLabel?: string;
}

export function ProjectNameDialog({
  open,
  onOpenChange,
  onSubmit,
  title = 'New Project',
  defaultValue = '',
  submitLabel = 'Create Project',
}: ProjectNameDialogProps) {
  const [name, setName] = useState(defaultValue);

  // Sync name when dialog opens with a new defaultValue
  useEffect(() => {
    if (open) {
      setName(defaultValue);
    }
  }, [open, defaultValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit(name.trim());
      setName('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="font-display">{title}</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <Label htmlFor="project-name" className="text-muted-foreground">
              Project Name
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Documentary Shoot, Music Video"
              className="mt-2"
              maxLength={NAME_VALIDATION.MAX_LENGTH}
              autoFocus
            />
            <div className="flex justify-between mt-1">
              <p className={`text-xs ${name.trim().length > 0 && !isValidName(name) ? 'text-destructive' : 'text-muted-foreground'}`}>
                {name.trim().length > 0 && !isValidName(name) ? `Minimum ${NAME_VALIDATION.MIN_LENGTH} characters` : ''}
              </p>
              <p className="text-xs text-muted-foreground">
                {name.length}/{NAME_VALIDATION.MAX_LENGTH}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValidName(name)}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
